// Órdenes de reparación — Taller → Operación → Órdenes (953).
// Lista con filtros y detalle de la orden como vista que reemplaza la lista (sin
// ventanas modales): trabajo y pruebas, presupuesto versionado (enviar por
// WhatsApp / portal Pit Stop, respuesta en mostrador), cobros (anticipo de
// cliente y entrega como venta real del POS con factura SRI), fotos, recepción,
// línea de tiempo y ticket imprimible con el QR del portal.
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';
import {
  AlertTriangle, ArrowLeft, ShieldCheck, Lightbulb, Camera, Check, ClipboardList, Copy, Eye, EyeOff, MessageCircle, Plus, Printer, Receipt, Search, Send, Trash2, Wrench, Zap,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { getErrorMessage } from '@/lib/api/client';
import { useTabsStore } from '@/stores/tabs.store';
import { Imprimible, PrintButton } from '@/components/print/DocPrint';
import { comprimirFoto } from '@/features/hotel/hkOffline';
import {
  Campo, fieldCls, money, useSort, SortTh, tallerRpc, QK, ESTADOS, urlPitStop, urlPasaporte, urlWhatsApp, fmtFecha, fmtFechaHora, totalLinea, PanelIntegrado, useDialogoEnLinea,
  CHECKS_FISICOS, PRUEBAS_ENCENDIDO, TIPOS_EQUIPO,
  type OrdenFila, type OrdenDetalle, type Linea, type Catalogo, type ConfigTaller, type EstadoOrden,
} from './shared';
import { TurnoInline } from './caja';
import { AvisoDictado, BenchForm, CertificadoBench, type Pruebas } from './bench';

const FILTROS: [string, string][] = [['abiertas', 'Abiertas'], ['presupuestado', 'Presupuesto enviado'], ['reparacion', 'En reparación'], ['listo', 'Listas'], ['entregado', 'Entregadas'], ['todas', 'Todas']];

export function OrdenesTaller() {
  const openTab = useTabsStore((s) => s.openTab);
  const activeTabId = useTabsStore((s) => s.activeTabId);
  const [filtro, setFiltro] = useState('abiertas');
  const [q, setQ] = useState('');
  const [detalle, setDetalle] = useState<string | null>(null);
  // El tablero pide abrir una orden concreta.
  useEffect(() => {
    try { const id = sessionStorage.getItem('taller_abrir_orden'); if (id) { sessionStorage.removeItem('taller_abrir_orden'); setDetalle(id); } } catch { /* sin almacenamiento */ }
  }, [activeTabId]);
  const { data = [], isLoading } = useQuery<OrdenFila[]>({
    queryKey: [QK, 'ordenes', filtro, q],
    queryFn: () => tallerRpc('ordenes_listar', { _estado: filtro === 'todas' ? null : filtro, _q: q || null }),
    refetchInterval: 30000,
  });
  const sort = useSort<OrdenFila>(data, (r, k) => (k === 'codigo' ? r.codigo : k === 'estado' ? ESTADOS[r.estado].paso : k === 'cliente' ? r.cliente : k === 'equipo' ? r.equipo
    : k === 'prometida' ? r.fecha_prometida : k === 'total' ? Number(r.total ?? 0) : k === 'creada' ? r.created_at : null));
  const totales = useMemo(() => ({ total: data.reduce((a, r) => a + Number(r.total ?? 0), 0), anticipo: data.reduce((a, r) => a + Number(r.anticipo), 0) }), [data]);

  if (detalle) return <OrdenDetalleVista id={detalle} onClose={() => setDetalle(null)} />;

  return (
    <div className="space-y-3 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="flex items-center gap-2 text-xl font-semibold"><Wrench className="h-5 w-5" /> Órdenes de reparación</h1>
        <Button size="sm" onClick={() => openTab({ id: '/taller/recepcion', title: 'Nueva recepción', path: '/taller/recepcion' })}><Plus className="mr-1 h-4 w-4" /> Nueva recepción</Button>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {FILTROS.map(([k, l]) => (
          <button key={k} type="button" onClick={() => setFiltro(k)}
            className={`rounded-full px-3 py-1.5 text-sm ${filtro === k ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>{l}</button>
        ))}
        <div className="relative ml-auto">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <input className={`${fieldCls} w-64 pl-7`} placeholder="Código, cliente o número de serie" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
      </div>
      <Card>
        <CardContent className="overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pl-3 pr-2">N°</th>
              <SortTh col="codigo" sort={sort}>Orden</SortTh>
              <SortTh col="estado" sort={sort}>Estado</SortTh>
              <SortTh col="cliente" sort={sort}>Cliente</SortTh>
              <SortTh col="equipo" sort={sort}>Equipo</SortTh>
              <th className="py-2 pr-2">Falla</th>
              <th className="py-2 pr-2">Técnico</th>
              <SortTh col="prometida" sort={sort}>Prometida</SortTh>
              <SortTh col="total" sort={sort} className="text-right">Aprobado</SortTh>
              <th className="py-2 pr-3 text-right">Anticipo</th>
            </tr></thead>
            <tbody>
              {isLoading && <tr><td colSpan={10} className="py-8 text-center text-muted-foreground">Cargando…</td></tr>}
              {!isLoading && sort.sorted.length === 0 && <tr><td colSpan={10} className="py-8 text-center text-muted-foreground">Sin órdenes.</td></tr>}
              {sort.sorted.map((o, i) => (
                <tr key={o.id} className="cursor-pointer border-b last:border-0 hover:bg-muted/40" onClick={() => setDetalle(o.id)}>
                  <td className="py-2 pl-3 pr-2 text-muted-foreground">{i + 1}</td>
                  <td className="py-2 pr-2 font-mono font-semibold">
                    {o.codigo}
                    {o.prioridad === 'urgente' && <Zap className="ml-1 inline h-3.5 w-3.5 text-amber-500" aria-label="Urgente" />}
                    {o.reingreso && <span className="ml-1 rounded bg-rose-100 px-1 text-[10px] text-rose-800 dark:bg-rose-900/40 dark:text-rose-300">Reingreso</span>}
                  </td>
                  <td className="py-2 pr-2"><span className={`rounded-full px-2 py-0.5 text-xs ${ESTADOS[o.estado].cls}`}>{ESTADOS[o.estado].label}</span>
                    {o.dias_listo != null && o.dias_listo > 0 && <div className="text-[11px] text-orange-600">hace {o.dias_listo} día(s)</div>}</td>
                  <td className="py-2 pr-2">{o.cliente}</td>
                  <td className="py-2 pr-2">{o.equipo}{o.serial && <div className="font-mono text-[11px] text-muted-foreground">{o.serial}</div>}</td>
                  <td className="max-w-xs truncate py-2 pr-2" title={o.falla}>{o.falla}</td>
                  <td className="py-2 pr-2">{o.tecnico ?? '—'}</td>
                  <td className={`py-2 pr-2 ${o.atrasada ? 'font-semibold text-red-600' : ''}`}>{fmtFecha(o.fecha_prometida)}</td>
                  <td className="py-2 pr-2 text-right tabular-nums">{o.total ? money(Number(o.total)) : '—'}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{Number(o.anticipo) ? money(Number(o.anticipo)) : '—'}</td>
                </tr>
              ))}
            </tbody>
            {data.length > 0 && <tfoot><tr className="border-t-2 bg-muted/30 font-semibold">
              <td colSpan={8} className="py-2 pl-3">Total: {data.length} orden(es)</td>
              <td className="py-2 pr-2 text-right tabular-nums">{money(totales.total)}</td>
              <td className="py-2 pr-3 text-right tabular-nums">{money(totales.anticipo)}</td>
            </tr></tfoot>}
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

type Pestana = 'trabajo' | 'presupuesto' | 'cobros' | 'fotos' | 'recepcion' | 'pasaporte' | 'historia';
type LineaBorrador = Omit<Linea, 'id' | 'estado' | 'serial_repuesto' | 'precio_lista'> & { key: string };
const totalBorrador = (l: LineaBorrador, desc = 0) => (l.en_garantia || l.en_plan ? 0
  : totalLinea(l.tipo === 'mano_obra' && desc > 0 ? { ...l, precio: Math.round(l.precio * (1 - desc / 100) * 10000) / 10000 } : l));
type PagoForm = { forma_pago_id: string; monto: string; referencia: string };

export function OrdenDetalleVista({ id, onClose }: { id: string; onClose: () => void }) {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const [tab, setTab] = useState<Pestana>('trabajo');
  const [ticket, setTicket] = useState(false);
  const { data: o, isLoading } = useQuery<OrdenDetalle>({ queryKey: [QK, 'orden', id], queryFn: () => tallerRpc('orden_obtener', { _id: id }), refetchInterval: 20000 });
  const { data: cat } = useQuery<Catalogo>({ queryKey: [QK, 'catalogo'], queryFn: () => tallerRpc('catalogo') });
  const { data: cfg } = useQuery<ConfigTaller>({ queryKey: [QK, 'config'], queryFn: () => tallerRpc('config_get') });
  const inv = () => void qc.invalidateQueries({ queryKey: [QK] });
  const estado = useMutation({
    mutationFn: (x: { estado: EstadoOrden; nota?: string | null }) => tallerRpc('orden_estado', { _id: id, _estado: x.estado, _nota: x.nota ?? null, _publica: true }),
    onSuccess: () => { toast.success('Estado actualizado'); inv(); }, onError: (e) => toast.error(getErrorMessage(e)),
  });
  const actualizar = useMutation({
    mutationFn: (d: Record<string, unknown>) => tallerRpc('orden_actualizar', { _id: id, _d: d }),
    onSuccess: () => { toast.success('Guardado'); inv(); }, onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (isLoading || !o) return <div className="p-6 text-muted-foreground">Cargando…</div>;
  if (ticket) return <TicketOrden o={o} nombre={cfg?.config?.nombre_comercial ?? 'Taller'} terminos={cfg?.config?.terminos ?? null} onClose={() => setTicket(false)} />;

  const url = urlPitStop(o.token);
  const abierta = !['entregado', 'devuelto', 'abandonado'].includes(o.estado);
  const mensaje = `Hola ${o.cliente.nombre.split(' ')[0]}, sigue la reparación de tu ${[o.equipo.marca, o.equipo.modelo].filter(Boolean).join(' ')} (orden ${o.codigo}) en vivo aquí: ${url}`;
  const acciones: { label: string; estado: EstadoOrden; principal?: boolean; pedirNota?: boolean }[] = [];
  if (o.estado === 'recibido') acciones.push({ label: 'Pasar a diagnóstico', estado: 'diagnostico', principal: true });
  if (o.estado === 'aprobado') acciones.push({ label: 'Iniciar reparación', estado: 'reparacion', principal: true });
  if (o.estado === 'reparacion') acciones.push({ label: 'Esperando repuesto', estado: 'esperando_repuesto', pedirNota: true }, { label: 'Pasar a pruebas', estado: 'pruebas', principal: true });
  if (o.estado === 'esperando_repuesto') acciones.push({ label: 'Volver a reparación', estado: 'reparacion', principal: true });
  if (o.estado === 'pruebas') acciones.push({ label: 'Volver a reparación', estado: 'reparacion' }, { label: 'Marcar listo', estado: 'listo', principal: true });
  if (o.estado === 'listo') acciones.push({ label: 'Volver a pruebas', estado: 'pruebas' });

  const cambiar = async (a: (typeof acciones)[number]) => {
    let nota: string | null = null;
    if (a.pedirNota) {
      const v = await dlg.pedir(a.label, [{ key: 'nota', label: 'Nota para el cliente (opcional)', placeholder: 'p. ej. llega el repuesto el viernes', minimo: 0 }], { aceptar: 'Guardar' });
      if (!v) return; nota = v.nota || null;
    }
    estado.mutate({ estado: a.estado, nota });
  };

  return (
    <div className="space-y-3 p-4 sm:p-6">
      {dlg.ui}
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="outline" size="sm" onClick={onClose}><ArrowLeft className="mr-1 h-4 w-4" /> Órdenes</Button>
        <h1 className="font-mono text-xl font-semibold">{o.codigo}</h1>
        {o.interna && <span className="rounded-full bg-zinc-200 px-2 py-0.5 text-xs text-zinc-800 dark:bg-zinc-700 dark:text-zinc-100">Orden interna · reacondicionamiento de usado</span>}
        {o.plan && <span className="inline-flex items-center gap-1 rounded-full bg-violet-100 px-2 py-0.5 text-xs text-violet-800 dark:bg-violet-900/40 dark:text-violet-200">
          <ShieldCheck className="h-3 w-3" /> {o.plan.plan} · {o.plan.usados}/{o.plan.mantenimientos} mant.</span>}
        <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${ESTADOS[o.estado].cls}`}>{ESTADOS[o.estado].label}</span>
        {o.prioridad === 'urgente' && <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-800"><Zap className="h-3 w-3" /> Urgente</span>}
        <div className="ml-auto flex flex-wrap gap-2">
          {abierta && acciones.map((a) => (
            <Button key={a.label} size="sm" variant={a.principal ? 'default' : 'outline'} isLoading={estado.isPending} onClick={() => void cambiar(a)}>{a.label}</Button>
          ))}
          <Button size="sm" variant="outline" onClick={() => setTicket(true)}><Printer className="mr-1 h-4 w-4" /> Ticket</Button>
        </div>
      </div>

      {o.reingreso_de && <GarantiaBanner o={o} responsable={cat?.tecnicos.find((t) => t.id === o.tecnico_responsable_id)?.nombre ?? null} />}
      {o.eventos.some((e) => (e.nota ?? '').startsWith('ATENCIÓN')) && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
          {o.eventos.find((e) => (e.nota ?? '').startsWith('ATENCIÓN'))?.nota} — verifique la procedencia del equipo.
        </div>
      )}

      <div className="grid gap-3 lg:grid-cols-[1fr,280px]">
        <Card><CardContent className="grid gap-3 p-4 text-sm sm:grid-cols-3">
          <div><div className="text-xs text-muted-foreground">Cliente</div><div className="font-medium">{o.cliente.nombre}</div>
            <div className="text-xs text-muted-foreground">{o.cliente.identificacion} · {o.cliente.celular ?? 'sin celular'}</div></div>
          <div><div className="text-xs text-muted-foreground">Equipo</div><div className="font-medium">{[o.equipo.marca, o.equipo.modelo].filter(Boolean).join(' ')}</div>
            <div className="font-mono text-xs text-muted-foreground">{o.equipo.serial ?? 'sin serie'}{o.equipo.color ? ` · ${o.equipo.color}` : ''}</div></div>
          <div><div className="text-xs text-muted-foreground">Falla reportada</div><div>{o.falla_reportada}</div></div>
          <Campo label="Técnico">
            <select className={fieldCls} disabled={!abierta} value={o.tecnico_id ?? ''} onChange={(e) => actualizar.mutate({ tecnico_id: e.target.value || null })}>
              <option value="">— Sin asignar —</option>
              {(cat?.tecnicos ?? []).filter((t) => t.activo).map((t) => <option key={t.id} value={t.id}>{t.nombre}{t.bancos.length ? ` (${t.bancos.join(', ')})` : ''}</option>)}
            </select>
          </Campo>
          <Campo label="Fecha prometida"><input type="date" className={fieldCls} disabled={!abierta} value={o.fecha_prometida ?? ''} onChange={(e) => actualizar.mutate({ fecha_prometida: e.target.value || null })} /></Campo>
          <div><div className="text-xs text-muted-foreground">Recibido</div><div>{fmtFechaHora(o.created_at)}</div>
            {o.garantia_hasta && <div className="text-xs text-emerald-700 dark:text-emerald-400">Garantía hasta {fmtFecha(o.garantia_hasta)}</div>}</div>
        </CardContent></Card>
        <Card><CardContent className="space-y-2 p-4 text-center text-sm">
          <div className="text-xs font-semibold">Portal del gamer · Pit Stop</div>
          <div className="mx-auto w-fit rounded bg-white p-2"><QRCodeSVG value={url} size={120} /></div>
          <div className="flex justify-center gap-1">
            <Button size="sm" variant="outline" onClick={() => { void navigator.clipboard?.writeText(url); toast.success('Enlace copiado'); }}><Copy className="mr-1 h-3.5 w-3.5" /> Copiar</Button>
            <a href={urlWhatsApp(o.cliente.celular, mensaje)} target="_blank" rel="noreferrer"><Button size="sm"><MessageCircle className="mr-1 h-3.5 w-3.5" /> WhatsApp</Button></a>
          </div>
          <a href={url} target="_blank" rel="noreferrer" className="block text-xs text-primary underline">Ver lo que ve el cliente</a>
        </CardContent></Card>
      </div>

      <div className="flex flex-wrap gap-1 border-b">
        {([['trabajo', 'Trabajo y pruebas'], ['presupuesto', `Presupuesto${o.aprobado.total ? ` · ${money(o.aprobado.total)}` : ''}`], ['cobros', 'Cobros y entrega'],
           ['fotos', `Fotos (${o.fotos.length})`], ['recepcion', 'Recepción'], ['pasaporte', 'Pasaporte'], ['historia', 'Línea de tiempo']] as [Pestana, string][]).map(([k, l]) => (
          <button key={k} type="button" onClick={() => setTab(k)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm ${tab === k ? 'border-primary font-medium text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>{l}</button>
        ))}
      </div>

      {tab === 'trabajo' && <TrabajoTab o={o} abierta={abierta} onGuardar={(d) => actualizar.mutate(d)} guardando={actualizar.isPending} />}
      {tab === 'presupuesto' && <PresupuestoTab o={o} cat={cat} cfg={cfg} mensaje={mensaje} />}
      {tab === 'cobros' && <CobrosTab o={o} cfg={cfg} />}
      {tab === 'fotos' && <FotosTab o={o} />}
      {tab === 'recepcion' && <RecepcionTab o={o} />}
      {tab === 'pasaporte' && <PasaporteTab o={o} />}
      {tab === 'historia' && <HistoriaTab o={o} abierta={abierta} onNota={(nota, publica) => actualizar.mutate({ nota, publica })} />}
    </div>
  );
}

// ── Trabajo y pruebas ──────────────────────────────────────────────────────
function TrabajoTab({ o, abierta, onGuardar, guardando }: { o: OrdenDetalle; abierta: boolean; onGuardar: (d: Record<string, unknown>) => void; guardando: boolean }) {
  const [diag, setDiag] = useState(o.diagnostico ?? '');
  const [trabajo, setTrabajo] = useState(o.trabajo_realizado ?? '');
  const [p, setP] = useState<Pruebas>(() => ({ ...(o.pruebas ?? {}) }));
  const guardarTodo = () => tallerRpc('orden_actualizar', { _id: o.id, _d: { diagnostico: diag, trabajo_realizado: trabajo, pruebas: p } });
  return (
    <Card><CardContent className="space-y-3 p-4 text-sm">
      <AvisoDictado />
      <Campo label="Diagnóstico (lo que encontró el técnico)"><textarea className={`${fieldCls} h-20`} disabled={!abierta} value={diag} onChange={(e) => setDiag(e.target.value)} /></Campo>
      <Campo label="Trabajo realizado (se muestra al cliente al quedar listo)"><textarea className={`${fieldCls} h-20`} disabled={!abierta} value={trabajo} onChange={(e) => setTrabajo(e.target.value)} /></Campo>
      <BenchForm tipo={o.equipo.tipo} valor={p} onChange={setP} disabled={!abierta} />
      {abierta && (
        <div className="flex justify-end">
          <Button size="sm" isLoading={guardando} onClick={() => onGuardar({ diagnostico: diag, trabajo_realizado: trabajo, pruebas: p })}><Check className="mr-1 h-4 w-4" /> Guardar</Button>
        </div>
      )}
      <RepuestosInstalados o={o} abierta={abierta} />
      <CertificadoBench ordenId={o.id} estadoOrden={o.estado} guardarAntes={abierta ? guardarTodo : undefined} />
    </CardContent></Card>
  );
}

// ── Presupuesto ────────────────────────────────────────────────────────────
function PresupuestoTab({ o, cat, cfg, mensaje }: { o: OrdenDetalle; cat?: Catalogo; cfg?: ConfigTaller; mensaje: string }) {
  const qc = useQueryClient();
  const borrador = o.presupuestos.find((p) => p.estado === 'borrador');
  const enviado = o.presupuestos.find((p) => p.estado === 'enviado');
  const cubre = o.garantia_dictamen === 'cubre';
  const sinDictamen = !!o.reingreso_de && !o.garantia_dictamen;
  // en garantía el precio guardado es 0: se edita sobre el precio de lista
  const [lineas, setLineas] = useState<LineaBorrador[]>(() => (borrador?.lineas ?? []).map(({ serial_repuesto: _s, precio_lista, ...l }) =>
    ({ ...l, precio: precio_lista != null ? Number(precio_lista) : l.precio, key: l.id })));
  const plan = o.plan;
  const cupoPlan = !!plan && plan.usados < plan.mantenimientos;
  const descPlan = Number(plan?.descuento_mo_pct ?? 0);
  // la falla de mantenimiento del plan entra sin costo mientras haya cupo
  const enPlan = (tipo: Linea['tipo'], fallaId: string | null) => cupoPlan && tipo !== 'diagnostico' && !cubre && !o.interna
    && (!plan?.falla_mantenimiento_id || plan.falla_mantenimiento_id === fallaId);
  const extra = (tipo: Linea['tipo'], fallaId: string | null = null) => {
    const p = enPlan(tipo, fallaId) && !!plan?.falla_mantenimiento_id;
    return { original: true, en_plan: p, en_garantia: !p && (o.interna || (cubre && tipo !== 'diagnostico')) };
  };
  const { data: sugerencias = [] } = useQuery<{ falla_id: string; nombre: string; precio: number; minutos: number; veces: number; exito_pct: number | null; puntaje: number; razon: string }[]>({
    queryKey: [QK, 'sugerencias', o.id], queryFn: () => tallerRpc('sugerir_fallas', { _orden: o.id }), enabled: !o.interna,
  });
  const modeloId = o.equipo.modelo_id;
  // compatibilidad: true = compatible, false = registrado y NO incluye el modelo, null = sin registro
  const compatible = (productoId: string | null) => {
    const r = cat?.repuestos.find((x) => x.id === productoId);
    if (!r || !modeloId || !r.modelos?.length) return null;
    return r.modelos.includes(modeloId);
  };
  const repuestosOrden = useMemo(() => [...(cat?.repuestos ?? [])].sort((a, b) => Number(compatible(b.id) === true) - Number(compatible(a.id) === true)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cat, modeloId]);
  const [falla, setFalla] = useState('');
  const [rep, setRep] = useState('');
  const [aprobar, setAprobar] = useState<string[]>(() => (enviado?.lineas ?? []).map((l) => l.id));
  const [nombre, setNombre] = useState('');
  const editable = !['entregado', 'devuelto', 'rechazado'].includes(o.estado);
  const fallas = (cat?.fallas ?? []).filter((f) => f.activo && f.tipo === o.equipo.tipo);
  const inv = () => void qc.invalidateQueries({ queryKey: [QK] });
  const k = () => Math.random().toString(36).slice(2);
  const agregarFalla = (id: string = falla) => {
    const f = fallas.find((x) => x.id === id); if (!f) return;
    const nuevas: LineaBorrador[] = [{ key: k(), tipo: 'mano_obra', falla_id: f.id, producto_id: null, descripcion: f.nombre, cantidad: 1, precio: Number(f.precio_mano_obra), iva_pct: 15, garantia_dias: f.garantia_dias, ...extra('mano_obra', f.id) }];
    f.repuestos.forEach((r) => {
      const p = cat?.repuestos.find((x) => x.id === r.producto_id);
      if (p) nuevas.push({ key: k(), tipo: 'repuesto', falla_id: f.id, producto_id: p.id, descripcion: p.nombre, cantidad: r.cantidad, precio: Number(p.precio), iva_pct: Number(p.iva), garantia_dias: null, ...extra('repuesto', f.id) });
    });
    setLineas([...lineas, ...nuevas]); setFalla('');
  };
  const agregarRepuesto = () => {
    const p = cat?.repuestos.find((x) => x.id === rep); if (!p) return;
    if (compatible(p.id) === false) toast.warning(`«${p.nombre}» no consta como compatible con ${[o.equipo.marca, o.equipo.modelo].filter(Boolean).join(' ')}`);
    setLineas([...lineas, { key: k(), tipo: 'repuesto', falla_id: null, producto_id: p.id, descripcion: p.nombre, cantidad: 1, precio: Number(p.precio), iva_pct: Number(p.iva), garantia_dias: null, ...extra('repuesto') }]); setRep('');
  };
  const guardar = useMutation({
    mutationFn: async (enviar: boolean) => {
      await tallerRpc('presupuesto_guardar', { _orden: o.id, _lineas: lineas.map(({ key: _k, ...l }) => l) });
      if (!enviar) return 'borrador';
      const r = await tallerRpc<{ garantia: boolean }>('presupuesto_enviar', { _orden: o.id });
      return r.garantia ? 'garantia' : 'enviado';
    },
    onSuccess: (r) => {
      toast.success(r === 'garantia' ? 'Reparación en garantía aprobada: ya puede iniciar el trabajo' : r === 'enviado' ? 'Presupuesto enviado: comparta el enlace por WhatsApp' : 'Borrador guardado');
      inv();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const responder = useMutation({
    mutationFn: () => tallerRpc('presupuesto_responder', { _orden: o.id, _aprobadas: aprobar, _nombre: nombre }),
    onSuccess: () => { toast.success('Respuesta registrada'); inv(); }, onError: (e) => toast.error(getErrorMessage(e)),
  });
  const totalB = lineas.reduce((a, l) => a + totalBorrador(l, descPlan), 0);
  const todoGarantia = lineas.length > 0 && lineas.every((l) => l.en_garantia || l.en_plan);
  const tarifaDiag = Number(cfg?.config?.tarifa_diagnostico ?? 0);

  return (
    <div className="space-y-3">
      {o.presupuestos.filter((p) => p.estado !== 'borrador').map((p) => (
        <Card key={p.id}><CardContent className="p-3 text-sm">
          <div className="mb-1 flex flex-wrap items-center gap-2 text-xs">
            <span className="font-semibold">Versión {p.version}{p.adicional ? ' · adicional' : ''}</span>
            <span className={`rounded px-1.5 ${p.estado === 'aprobado' ? 'bg-emerald-100 text-emerald-800' : p.estado === 'enviado' ? 'bg-amber-100 text-amber-800' : p.estado === 'parcial' ? 'bg-sky-100 text-sky-800' : 'bg-rose-100 text-rose-800'}`}>{p.estado}</span>
            {p.respondido_at && <span className="text-muted-foreground">respondido {fmtFechaHora(p.respondido_at)} {p.canal ? `· ${p.canal}` : ''}{p.evidencia?.nombre ? ` · ${String(p.evidencia.nombre)}` : ''}</span>}
          </div>
          <table className="w-full text-xs">
            <tbody>{p.lineas.map((l) => (
              <tr key={l.id} className="border-b last:border-0">
                <td className="py-1 pr-2">{p.estado === 'enviado'
                  ? <input type="checkbox" checked={aprobar.includes(l.id)} onChange={(e) => setAprobar(e.target.checked ? [...aprobar, l.id] : aprobar.filter((x) => x !== l.id))} />
                  : l.estado === 'aprobada' ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : l.estado === 'rechazada' ? '✕' : ''}</td>
                <td className="py-1 pr-2 text-muted-foreground">{l.tipo === 'mano_obra' ? 'Mano de obra' : l.tipo === 'repuesto' ? 'Repuesto' : 'Diagnóstico'}</td>
                <td className="py-1 pr-2">{l.descripcion}{l.garantia_dias ? <span className="text-muted-foreground"> · garantía {l.garantia_dias} d</span> : null}
                  {l.en_garantia && <span className="ml-1 rounded bg-emerald-100 px-1 text-[10px] text-emerald-800">sin costo · garantía{l.precio_lista ? ` (${money(Number(l.precio_lista))})` : ''}</span>}
                  {l.en_plan && <span className="ml-1 rounded bg-violet-100 px-1 text-[10px] text-violet-800">incluido en el plan</span>}
                  {l.tipo === 'repuesto' && !l.original && <span className="ml-1 rounded bg-amber-100 px-1 text-[10px] text-amber-800">alternativo</span>}</td>
                <td className="py-1 pr-2 text-right tabular-nums">{Number(l.cantidad)} × {money(Number(l.precio))}</td>
                <td className="py-1 text-right tabular-nums">{money(totalLinea(l))}</td>
              </tr>
            ))}</tbody>
          </table>
          {p.estado === 'enviado' && (
            <div className="mt-2 flex flex-wrap items-end gap-2 border-t pt-2">
              <span className="text-xs text-muted-foreground">El cliente puede aprobarlo en el portal, o registre aquí su respuesta en el mostrador:</span>
              <input className={`${fieldCls} w-56`} placeholder="Nombre de quien aprueba" value={nombre} onChange={(e) => setNombre(e.target.value)} />
              <Button size="sm" disabled={nombre.trim().length < 3} isLoading={responder.isPending} onClick={() => responder.mutate()}>Registrar respuesta ({aprobar.length} aprobadas)</Button>
              <a href={urlWhatsApp(o.cliente.celular, `${mensaje} — tu presupuesto está listo para aprobar.`)} target="_blank" rel="noreferrer">
                <Button size="sm" variant="outline"><MessageCircle className="mr-1 h-3.5 w-3.5" /> Reenviar por WhatsApp</Button></a>
            </div>
          )}
        </CardContent></Card>
      ))}

      {editable && !enviado && sinDictamen && (
        <div className="rounded-md border border-rose-300 bg-rose-50 p-3 text-sm text-rose-900 dark:bg-rose-950/30 dark:text-rose-200">
          Es un reingreso: el jefe de taller debe dar el dictamen de garantía (arriba) antes de armar el presupuesto.
        </div>
      )}
      {editable && !enviado && !sinDictamen && (
        <Card><CardContent className="space-y-3 p-4 text-sm">
          {plan && !o.interna && <div className="rounded-md bg-violet-50 px-2 py-1 text-xs text-violet-900 dark:bg-violet-950/30 dark:text-violet-200">
            Plan «{plan.plan}» vigente hasta {fmtFecha(plan.fin)}: {plan.usados} de {plan.mantenimientos} mantenimientos usados
            {cupoPlan ? ' (el mantenimiento del plan entra sin costo)' : ' — cupo agotado'}{descPlan > 0 ? ` · ${descPlan}% de descuento en mano de obra (se aplica al guardar)` : ''}.</div>}
          {sugerencias.length > 0 && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-xs dark:border-amber-800 dark:bg-amber-950/30">
              <div className="mb-1 flex items-center gap-1 font-semibold"><Lightbulb className="h-3.5 w-3.5" /> Diagnóstico sugerido (historial del taller y síntomas)</div>
              <div className="flex flex-wrap gap-1">
                {sugerencias.map((g) => (
                  <button key={g.falla_id} type="button" onClick={() => agregarFalla(g.falla_id)} title={g.razon}
                    className="rounded-full border border-amber-400 bg-background px-2 py-0.5 hover:bg-amber-100 dark:hover:bg-amber-900/40">
                    + {g.nombre} · {money(Number(g.precio))}{g.veces > 0 ? ` · ${g.veces} caso(s)` : ''}{g.exito_pct != null ? ` · ${g.exito_pct}% sin reingreso` : ''}
                  </button>
                ))}
              </div>
            </div>
          )}
          {cubre && <div className="rounded-md bg-emerald-50 px-2 py-1 text-xs text-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-200">
            Reingreso cubierto: las líneas marcadas «Garantía» van sin costo para el cliente (los repuestos salen como gasto de garantía). Desmarque las que sean trabajo nuevo cobrable.</div>}
          <div className="font-semibold">{o.presupuestos.some((p) => ['aprobado', 'parcial'].includes(p.estado)) ? 'Presupuesto adicional (trabajo nuevo: requiere aprobación)' : 'Armar presupuesto'}</div>
          <div className="flex flex-wrap items-end gap-2">
            <Campo label="Agregar desde el catálogo de fallas">
              <select className={`${fieldCls} w-80`} value={falla} onChange={(e) => setFalla(e.target.value)}>
                <option value="">— Falla ({TIPOS_EQUIPO.find(([t]) => t === o.equipo.tipo)?.[1] ?? o.equipo.tipo}) —</option>
                {fallas.map((f) => <option key={f.id} value={f.id}>{f.nombre}{f.modelo ? ` · ${f.modelo}` : ''} · {money(Number(f.precio_mano_obra))}</option>)}
              </select>
            </Campo>
            <Button size="sm" variant="outline" disabled={!falla} onClick={() => agregarFalla()}><Plus className="mr-1 h-4 w-4" /> Agregar</Button>
            <Campo label="Repuesto">
              <select className={`${fieldCls} w-72`} value={rep} onChange={(e) => setRep(e.target.value)}>
                <option value="">— Repuesto del inventario —</option>
                {repuestosOrden.map((p) => { const c = compatible(p.id); return <option key={p.id} value={p.id}>{c === true ? '✓ ' : c === false ? '⚠ ' : ''}{p.codigo} · {p.nombre} · {money(Number(p.precio))} (stock {Number(p.stock ?? 0)})</option>; })}
              </select>
            </Campo>
            <Button size="sm" variant="outline" disabled={!rep} onClick={agregarRepuesto}><Plus className="mr-1 h-4 w-4" /> Agregar</Button>
            <Button size="sm" variant="ghost" onClick={() => setLineas([...lineas, { key: k(), tipo: 'mano_obra', falla_id: null, producto_id: null, descripcion: 'Mano de obra', cantidad: 1, precio: 0, iva_pct: 15, garantia_dias: null, ...extra('mano_obra') }])}>+ Mano de obra libre</Button>
            {tarifaDiag > 0 && <Button size="sm" variant="ghost" onClick={() => setLineas([...lineas, { key: k(), tipo: 'diagnostico', falla_id: null, producto_id: null, descripcion: 'Diagnóstico técnico', cantidad: 1, precio: tarifaDiag, iva_pct: 15, garantia_dias: null, ...extra('diagnostico') }])}>+ Diagnóstico</Button>}
          </div>
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-1 pr-2">N°</th><th className="py-1 pr-2">Tipo</th><th className="py-1 pr-2">Descripción</th><th className="py-1 pr-2 text-right">Cant.</th><th className="py-1 pr-2 text-right">Precio s/IVA</th><th className="py-1 pr-2 text-right">Total</th><th /></tr></thead>
            <tbody>
              {lineas.length === 0 && <tr><td colSpan={7} className="py-4 text-center text-muted-foreground">Agregue la falla del catálogo: trae la mano de obra y los repuestos sugeridos.</td></tr>}
              {lineas.map((l, i) => (
                <tr key={l.key} className="border-b last:border-0">
                  <td className="py-1 pr-2 text-muted-foreground">{i + 1}</td>
                  <td className="py-1 pr-2 text-xs">{l.tipo === 'mano_obra' ? 'Mano de obra' : l.tipo === 'repuesto' ? 'Repuesto' : 'Diagnóstico'}
                    {plan && !cubre && !o.interna && l.tipo !== 'diagnostico' && (!plan.falla_mantenimiento_id || plan.falla_mantenimiento_id === l.falla_id) &&
                      <label className="mt-0.5 flex items-center gap-1 text-[11px] text-violet-700"><input type="checkbox" checked={l.en_plan} disabled={!cupoPlan && !l.en_plan}
                        onChange={(e) => setLineas(lineas.map((x) => ((l.falla_id && x.falla_id === l.falla_id) || x.key === l.key ? { ...x, en_plan: e.target.checked } : x)))} /> Plan</label>}
                    {cubre && l.tipo !== 'diagnostico' && <label className="mt-0.5 flex items-center gap-1 text-[11px] text-emerald-700"><input type="checkbox" checked={l.en_garantia}
                      onChange={(e) => setLineas(lineas.map((x) => (x.key === l.key ? { ...x, en_garantia: e.target.checked } : x)))} /> Garantía</label>}
                    {l.tipo === 'repuesto' && <select className="mt-0.5 block rounded border bg-background text-[11px]" value={l.original ? 'o' : 'a'}
                      onChange={(e) => setLineas(lineas.map((x) => (x.key === l.key ? { ...x, original: e.target.value === 'o' } : x)))}><option value="o">Original</option><option value="a">Alternativo</option></select>}</td>
                  <td className="py-1 pr-2"><input className={`${fieldCls} h-8`} value={l.descripcion} onChange={(e) => setLineas(lineas.map((x) => (x.key === l.key ? { ...x, descripcion: e.target.value } : x)))} />
                    {l.tipo === 'repuesto' && compatible(l.producto_id) === false && <div className="text-[11px] text-amber-700">⚠ No consta como compatible con este modelo</div>}</td>
                  <td className="py-1 pr-2"><input type="number" min={1} className={`${fieldCls} h-8 w-16 text-right`} value={l.cantidad} onChange={(e) => setLineas(lineas.map((x) => (x.key === l.key ? { ...x, cantidad: Number(e.target.value) } : x)))} /></td>
                  <td className="py-1 pr-2"><input type="number" step="0.01" min={0} className={`${fieldCls} h-8 w-24 text-right`} value={l.precio} onChange={(e) => setLineas(lineas.map((x) => (x.key === l.key ? { ...x, precio: Number(e.target.value) } : x)))} /></td>
                  <td className="py-1 pr-2 text-right tabular-nums">{totalBorrador(l, descPlan) !== totalLinea(l) ? <span><s className="text-muted-foreground">{money(totalLinea(l))}</s> {money(totalBorrador(l, descPlan))}</span> : money(totalLinea(l))}</td>
                  <td className="py-1 text-right"><button type="button" aria-label="Quitar" onClick={() => setLineas(lineas.filter((x) => x.key !== l.key))}><Trash2 className="h-4 w-4 text-muted-foreground hover:text-red-600" /></button></td>
                </tr>
              ))}
            </tbody>
            {lineas.length > 0 && <tfoot><tr className="border-t-2 font-semibold"><td colSpan={5} className="py-2">Total con IVA</td><td className="py-2 pr-2 text-right tabular-nums">{money(totalB)}</td><td /></tr></tfoot>}
          </table>
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" disabled={!lineas.length} isLoading={guardar.isPending} onClick={() => guardar.mutate(false)}>Guardar borrador</Button>
            <Button size="sm" disabled={!lineas.length} isLoading={guardar.isPending} onClick={() => guardar.mutate(true)}><Send className="mr-1 h-4 w-4" /> {todoGarantia ? 'Aprobar reparación en garantía' : 'Enviar al cliente'}</Button>
          </div>
        </CardContent></Card>
      )}
    </div>
  );
}

// ── Cobros: anticipo y entrega ─────────────────────────────────────────────
function PagosForm({ formas, pagos, setPagos }: { formas: ConfigTaller['formas']; pagos: PagoForm[]; setPagos: (p: PagoForm[]) => void }) {
  return (
    <div className="space-y-2">
      {pagos.map((p, i) => (
        <div key={i} className="flex flex-wrap items-end gap-2">
          <select className={`${fieldCls} w-56`} value={p.forma_pago_id} onChange={(e) => setPagos(pagos.map((x, j) => (j === i ? { ...x, forma_pago_id: e.target.value } : x)))}>
            <option value="">— Forma de pago —</option>
            {formas.map((f) => <option key={f.id} value={f.id}>{f.nombre}</option>)}
          </select>
          <input type="number" step="0.01" min={0} className={`${fieldCls} w-28 text-right`} placeholder="Monto" value={p.monto} onChange={(e) => setPagos(pagos.map((x, j) => (j === i ? { ...x, monto: e.target.value } : x)))} />
          <input className={`${fieldCls} w-44`} placeholder="Referencia / voucher" value={p.referencia} onChange={(e) => setPagos(pagos.map((x, j) => (j === i ? { ...x, referencia: e.target.value } : x)))} />
          {pagos.length > 1 && <button type="button" aria-label="Quitar" onClick={() => setPagos(pagos.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4 text-muted-foreground" /></button>}
        </div>
      ))}
      <Button size="sm" variant="ghost" onClick={() => setPagos([...pagos, { forma_pago_id: '', monto: '', referencia: '' }])}><Plus className="mr-1 h-4 w-4" /> Otro pago (pago mixto)</Button>
    </div>
  );
}

function CobrosTab({ o, cfg }: { o: OrdenDetalle; cfg?: ConfigTaller }) {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const formas = (cfg?.formas ?? []).filter((f) => f.id !== cfg?.config?.forma_anticipo_id && f.tipo !== 'credito');
  const nuevo = (monto = ''): PagoForm => ({ forma_pago_id: formas[0]?.id ?? '', monto, referencia: '' });
  const [ant, setAnt] = useState<PagoForm[]>(() => [nuevo(o.anticipo_sugerido ? String(o.anticipo_sugerido) : '')]);
  const devolver = o.estado === 'rechazado';
  const bodegaje = !devolver && o.bodegaje && !o.bodegaje.exonerado ? Number(o.bodegaje.monto) : 0;
  const totalEntrega = devolver ? Math.round(Number(cfg?.config?.tarifa_diagnostico ?? 0) * 1.15 * 100) / 100 : Math.round((Number(o.aprobado.total) + bodegaje) * 100) / 100;
  const saldo = Math.max(0, Math.round((totalEntrega - Number(o.anticipo_disponible)) * 100) / 100);
  const [ent, setEnt] = useState<PagoForm[]>(() => [nuevo(saldo ? String(saldo) : '')]);
  const inv = () => void qc.invalidateQueries({ queryKey: [QK] });
  const limpiar = (p: PagoForm[]) => p.filter((x) => x.forma_pago_id && Number(x.monto) > 0).map((x) => ({ forma_pago_id: x.forma_pago_id, monto: Number(x.monto), referencia: x.referencia || null }));
  const anticipo = useMutation({
    mutationFn: () => tallerRpc('anticipo_cobrar', { _orden: o.id, _pagos: limpiar(ant) }),
    onSuccess: () => { toast.success('Anticipo registrado (anticipo de cliente)'); inv(); }, onError: (e) => toast.error(getErrorMessage(e)),
  });
  const entregar = useMutation({
    mutationFn: () => tallerRpc<{ venta: { codigo?: string } | null; total: number }>('orden_entregar', { _orden: o.id, _pagos: limpiar(ent), _observacion: null }),
    onSuccess: (r) => { toast.success(r.venta ? `Entregado · venta ${r.venta.codigo ?? ''} por ${money(Number(r.total))} en el POS` : 'Entregado sin costo (garantía)'); inv(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const sumaEnt = limpiar(ent).reduce((a, x) => a + x.monto, 0);
  const sinCosto = !devolver && totalEntrega === 0 && o.presupuestos.some((p) => p.lineas.some((l) => (l.en_garantia || l.en_plan) && l.estado === 'aprobada'));
  const exonerar = useMutation({
    mutationFn: (motivo: string) => tallerRpc('bodegaje_exonerar', { _orden: o.id, _motivo: motivo }),
    onSuccess: () => { toast.success('Bodegaje exonerado'); void qc.invalidateQueries({ queryKey: [QK] }); }, onError: (e) => toast.error(getErrorMessage(e)),
  });
  const abierta = !['entregado', 'devuelto', 'abandonado'].includes(o.estado);

  return (
    <div className="space-y-3">
      {dlg.ui}
      <Card><CardContent className="grid gap-3 p-4 text-sm sm:grid-cols-4">
        <div><div className="text-xs text-muted-foreground">Aprobado por el cliente</div><div className="text-lg font-semibold tabular-nums">{money(Number(o.aprobado.total))}</div></div>
        <div><div className="text-xs text-muted-foreground">Anticipo disponible</div><div className="text-lg font-semibold tabular-nums">{money(Number(o.anticipo_disponible))}</div></div>
        <div><div className="text-xs text-muted-foreground">Saldo a cobrar al entregar</div><div className="text-lg font-semibold tabular-nums">{money(saldo)}</div></div>
        <div>{o.factura ? <><div className="text-xs text-muted-foreground">Factura</div><div className="font-medium">{o.factura.serie ?? 'Por emitir'} · {o.factura.estado}</div></> : null}
          {o.costo_garantia != null && <><div className="text-xs text-muted-foreground">Costo de garantía (repuestos)</div><div className="font-medium tabular-nums">{money(Number(o.costo_garantia))}</div></>}</div>
      </CardContent></Card>

      {o.pagos.length > 0 && (
        <Card><CardContent className="overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-2 pl-3 pr-2">N°</th><th className="py-2 pr-2">Fecha</th><th className="py-2 pr-2">Tipo</th><th className="py-2 pr-2">Forma</th><th className="py-2 pr-2">Referencia</th><th className="py-2 pr-2">Estado</th><th className="py-2 pr-3 text-right">Monto</th></tr></thead>
            <tbody>{o.pagos.map((p, i) => (
              <tr key={p.id} className="border-b last:border-0">
                <td className="py-1.5 pl-3 pr-2 text-muted-foreground">{i + 1}</td><td className="py-1.5 pr-2">{fmtFechaHora(p.fecha)}</td>
                <td className="py-1.5 pr-2">{p.tipo === 'anticipo' ? 'Anticipo' : 'Saldo'}</td><td className="py-1.5 pr-2">{p.forma ?? '—'}</td>
                <td className="py-1.5 pr-2 text-xs">{p.referencia ?? ''}{p.error ? <span className="text-red-600"> · {p.error}</span> : null}</td>
                <td className="py-1.5 pr-2 text-xs">{p.estado}</td><td className="py-1.5 pr-3 text-right tabular-nums">{money(Number(p.monto))}</td>
              </tr>))}
            </tbody>
            <tfoot><tr className="border-t-2 bg-muted/30 font-semibold"><td colSpan={6} className="py-2 pl-3">Total</td><td className="py-2 pr-3 text-right tabular-nums">{money(o.pagos.reduce((a, p) => a + Number(p.monto), 0))}</td></tr></tfoot>
          </table>
        </CardContent></Card>
      )}

      {abierta && !devolver && o.estado !== 'listo' && (
        <PanelIntegrado title="Cobrar anticipo" onClose={() => setAnt([nuevo()])} wide>
          <TurnoInline motivo="Para cobrar el anticipo en efectivo se necesita el turno de la caja del taller abierto (con tarjeta o transferencia no hace falta)." />
          <p className="text-xs text-muted-foreground">Se registra como anticipo del cliente (no es ingreso): se descuenta solo al entregar. Sugerido: {money(Number(o.anticipo_sugerido))}.
            El cliente también puede pagarlo con tarjeta o De Una desde el portal Pit Stop.</p>
          <PagosForm formas={formas} pagos={ant} setPagos={setAnt} />
          <div className="flex justify-end"><Button size="sm" disabled={!limpiar(ant).length} isLoading={anticipo.isPending} onClick={() => anticipo.mutate()}><Receipt className="mr-1 h-4 w-4" /> Registrar anticipo</Button></div>
        </PanelIntegrado>
      )}

      {(o.estado === 'listo' || devolver) && (
        <PanelIntegrado title={devolver ? 'Devolver sin reparar (cobra el diagnóstico)' : 'Entregar y facturar'} onClose={() => setEnt([nuevo(saldo ? String(saldo) : '')])} wide>
          {!sinCosto && <TurnoInline />}
          <p className="text-xs text-muted-foreground">
            Se registra como venta en la caja del taller (factura electrónica SRI y descuento de repuestos del inventario).
            {Number(o.anticipo_disponible) > 0 && ` El anticipo de ${money(Number(o.anticipo_disponible))} se aplica solo.`}
          </p>
          {!devolver && o.bodegaje && o.bodegaje.dias_cobro > 0 && o.bodegaje.configurado && (
            <div className="flex flex-wrap items-center gap-2 rounded-md bg-amber-50 px-2 py-1 text-xs text-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
              Listo hace {o.bodegaje.dias_listo} días: bodegaje de {o.bodegaje.dias_cobro} día(s) × {money(Number(o.bodegaje.tarifa))} + IVA
              {o.bodegaje.exonerado ? ' — exonerado' : ` = ${money(Number(o.bodegaje.monto))} (se factura con la entrega)`}
              {!o.bodegaje.exonerado && <Button size="sm" variant="ghost" onClick={async () => {
                const v = await dlg.pedir('Exonerar el bodegaje', [{ key: 'motivo', label: 'Motivo', minimo: 5 }], { aceptar: 'Exonerar' });
                if (v) exonerar.mutate(v.motivo);
              }}>Exonerar</Button>}
            </div>
          )}
          <div className="text-sm">Total {money(totalEntrega)} · anticipo {money(Math.min(Number(o.anticipo_disponible), totalEntrega))} · <strong>saldo {money(saldo)}</strong></div>
          {saldo > 0 && <PagosForm formas={formas} pagos={ent} setPagos={setEnt} />}
          {saldo > 0 && sumaEnt + 0.004 < saldo && <div className="text-xs text-red-600">Faltan {money(saldo - sumaEnt)}: no se entrega con saldo pendiente.</div>}
          <div className="flex justify-end">
            <Button size="sm" disabled={saldo > 0 && sumaEnt + 0.004 < saldo} isLoading={entregar.isPending}
              onClick={async () => {
                const ok = sinCosto
                  ? await dlg.confirmar(o.interna ? '¿Terminar el reacondicionamiento?' : '¿Entregar sin costo?', { detalle: o.interna ? 'Los repuestos salen como consumo y el usado queda a la venta con su costo.' : 'No hay nada que cobrar: no se emite factura. Los repuestos salen del inventario como consumo (garantía o plan).', aceptar: o.interna ? 'Terminar' : 'Entregar sin costo' })
                  : await dlg.confirmar(devolver ? '¿Devolver el equipo sin reparar?' : '¿Entregar el equipo y facturar?', { detalle: `Se emitirá la factura por ${money(totalEntrega)} en el POS.`, aceptar: devolver ? 'Devolver y facturar' : 'Entregar y facturar' });
                if (ok) entregar.mutate();
              }}>
              <Check className="mr-1 h-4 w-4" /> {sinCosto ? (o.interna ? 'Terminar reacondicionamiento' : 'Entregar sin costo') : devolver ? 'Devolver y facturar diagnóstico' : 'Entregar y facturar'}
            </Button>
          </div>
        </PanelIntegrado>
      )}
    </div>
  );
}

// ── Garantía: dictamen del reingreso ───────────────────────────────────────
function GarantiaBanner({ o, responsable }: { o: OrdenDetalle; responsable: string | null }) {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const dictaminar = useMutation({
    mutationFn: (x: { cubre: boolean; motivo: string | null }) => tallerRpc('garantia_dictaminar', { _orden: o.id, _cubre: x.cubre, _motivo: x.motivo }),
    onSuccess: () => { toast.success('Dictamen registrado'); void qc.invalidateQueries({ queryKey: [QK] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const original = o.historial.find((h) => h.id === o.reingreso_de);
  const puede = !o.garantia_dictamen && ['recibido', 'diagnostico'].includes(o.estado);
  const noCubre = async () => {
    const v = await dlg.pedir('La garantía no cubre este reingreso', [{ key: 'motivo', label: 'Motivo de exclusión (lo verá el cliente)', placeholder: 'Golpe / líquido / sello roto / falla distinta a la reparada', minimo: 5, multilinea: true }], { aceptar: 'Registrar' });
    if (v) dictaminar.mutate({ cubre: false, motivo: v.motivo });
  };
  return (
    <div className={`space-y-2 rounded-md border px-3 py-2 text-sm ${o.garantia_dictamen === 'cubre' ? 'border-emerald-300 bg-emerald-50 text-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-200'
      : 'border-rose-300 bg-rose-50 text-rose-900 dark:border-rose-800 dark:bg-rose-950/30 dark:text-rose-200'}`}>
      {dlg.ui}
      <div className="flex flex-wrap items-center gap-2">
        <AlertTriangle className="h-4 w-4" />
        <span>Reingreso en garantía{original ? ` de ${original.codigo} (garantía hasta ${fmtFecha(original.garantia_hasta)})` : ''}.
          {responsable && <> Técnico responsable: <strong>{responsable}</strong>.</>}</span>
        {o.garantia_dictamen === 'cubre' && <span className="rounded bg-emerald-600 px-2 py-0.5 text-xs text-white">Cubre la garantía · sin costo</span>}
        {o.garantia_dictamen === 'no_cubre' && <span className="rounded bg-rose-600 px-2 py-0.5 text-xs text-white">No cubre: {o.garantia_motivo}</span>}
        {puede && (
          <span className="ml-auto flex gap-2">
            <Button size="sm" isLoading={dictaminar.isPending} onClick={async () => { if (await dlg.confirmar('¿La garantía cubre este reingreso?', { detalle: 'Las líneas irán sin costo para el cliente y el costo quedará cargado al técnico de la reparación original.', aceptar: 'Sí, cubre' })) dictaminar.mutate({ cubre: true, motivo: null }); }}>Cubre la garantía</Button>
            <Button size="sm" variant="outline" onClick={() => void noCubre()}>No cubre</Button>
          </span>
        )}
      </div>
      {puede && <div className="text-xs">Revise el historial y el estado físico (golpes, líquido, sellos) antes del dictamen. Lo da el jefe de taller (nivel 3).</div>}
    </div>
  );
}

// ── Repuestos instalados: original / alternativo y serie ───────────────────
function RepuestosInstalados({ o, abierta }: { o: OrdenDetalle; abierta: boolean }) {
  const qc = useQueryClient();
  const rep = o.presupuestos.flatMap((p) => p.lineas).filter((l) => l.tipo === 'repuesto' && l.estado === 'aprobada');
  const [series, setSeries] = useState<Record<string, string>>(() => Object.fromEntries(rep.map((l) => [l.id, l.serial_repuesto ?? ''])));
  const guardar = useMutation({
    mutationFn: (x: { id: string; original: boolean; serial: string }) => tallerRpc('linea_repuesto', { _linea: x.id, _original: x.original, _serial: x.serial || null }),
    onSuccess: () => { toast.success('Repuesto actualizado: queda en el pasaporte del equipo'); void qc.invalidateQueries({ queryKey: [QK, 'orden', o.id] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  if (!rep.length) return null;
  return (
    <div className="space-y-1 border-t pt-3">
      <div className="text-xs font-semibold text-muted-foreground">Repuestos instalados (quedan en el pasaporte del equipo)</div>
      <table className="w-full text-sm">
        <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-1 pr-2">N°</th><th className="py-1 pr-2">Repuesto</th><th className="py-1 pr-2">Tipo</th><th className="py-1 pr-2">Número de serie (repuestos caros)</th><th /></tr></thead>
        <tbody>{rep.map((l, i) => (
          <tr key={l.id} className="border-b last:border-0">
            <td className="py-1 pr-2 text-muted-foreground">{i + 1}</td>
            <td className="py-1 pr-2">{l.descripcion} × {Number(l.cantidad)}{l.en_garantia && <span className="ml-1 rounded bg-emerald-100 px-1 text-[10px] text-emerald-800">garantía</span>}</td>
            <td className="py-1 pr-2">
              <select className={`${fieldCls} h-8 w-36`} disabled={!abierta} value={l.original ? 'o' : 'a'}
                onChange={(e) => guardar.mutate({ id: l.id, original: e.target.value === 'o', serial: series[l.id] ?? '' })}>
                <option value="o">Original</option><option value="a">Alternativo</option>
              </select>
            </td>
            <td className="py-1 pr-2"><input className={`${fieldCls} h-8 w-56 font-mono uppercase`} disabled={!abierta} placeholder="APU / chip HDMI / placa…" value={series[l.id] ?? ''}
              onChange={(e) => setSeries({ ...series, [l.id]: e.target.value })} /></td>
            <td className="py-1 text-right">{abierta && (series[l.id] ?? '') !== (l.serial_repuesto ?? '') &&
              <Button size="sm" variant="outline" onClick={() => guardar.mutate({ id: l.id, original: l.original, serial: series[l.id] ?? '' })}>Guardar</Button>}</td>
          </tr>))}
        </tbody>
      </table>
    </div>
  );
}

// ── Pasaporte del equipo (historial por número de serie) y traspaso ────────
interface FichaPasaporte {
  token: string; traspaso: { codigo: string; expira: string } | null;
  duenos: { nombre: string; identificacion: string; desde: string; hasta: string | null; motivo: string; canal: string | null }[];
}
function PasaporteTab({ o }: { o: OrdenDetalle }) {
  const qc = useQueryClient();
  const [sticker, setSticker] = useState(false);
  const [q, setQ] = useState('');
  const [nuevo, setNuevo] = useState<{ id: string; nombre: string } | null>(null);
  const [codigo, setCodigo] = useState('');
  const { data: f } = useQuery<FichaPasaporte>({ queryKey: [QK, 'pasaporte', o.equipo.id], queryFn: () => tallerRpc('equipo_pasaporte', { _equipo: o.equipo.id }) });
  const { data: clientes = [] } = useQuery<{ id: string; nombre: string; identificacion: string }[]>({
    queryKey: [QK, 'clientes', q], queryFn: () => tallerRpc('clientes_buscar', { _q: q }), enabled: !nuevo && q.trim().length >= 2,
  });
  const inv = () => void qc.invalidateQueries({ queryKey: [QK] });
  const generar = useMutation({
    mutationFn: () => tallerRpc('traspaso_generar', { _equipo: o.equipo.id }),
    onSuccess: () => { toast.success('Código de traspaso generado (vale 7 días)'); inv(); }, onError: (e) => toast.error(getErrorMessage(e)),
  });
  const aplicar = useMutation({
    mutationFn: () => tallerRpc<{ ok: boolean; mensaje: string }>('traspaso_aplicar', { _equipo: o.equipo.id, _codigo: codigo, _partner: nuevo!.id }),
    onSuccess: (r) => { if (r.ok) { toast.success(r.mensaje); setNuevo(null); setCodigo(''); setQ(''); } else toast.error(r.mensaje); inv(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  if (!f) return <div className="p-4 text-sm text-muted-foreground">Cargando…</div>;
  const url = urlPasaporte(f.token);
  const equipo = [o.equipo.marca, o.equipo.modelo].filter(Boolean).join(' ');
  if (sticker) return (
    <div className="space-y-3">
      <div className="no-print flex gap-2"><Button size="sm" variant="outline" onClick={() => setSticker(false)}><ArrowLeft className="mr-1 h-4 w-4" /> Volver</Button><PrintButton /></div>
      <div className="mx-auto max-w-[260px] rounded border bg-white p-3 text-center font-mono text-[11px] text-black">
        <Imprimible titulo="Pasaporte del equipo" subtitulo={equipo}>
          <div className="flex flex-col items-center gap-1"><QRCodeSVG value={url} size={130} /><div>S/N {o.equipo.serial ?? '—'}</div><div className="text-[10px]">Escanee: historial, repuestos y garantía</div></div>
        </Imprimible>
      </div>
    </div>
  );
  return (
    <div className="grid gap-3 lg:grid-cols-[260px,1fr]">
      <Card><CardContent className="space-y-2 p-4 text-center text-sm">
        <div className="text-xs font-semibold">Pasaporte del equipo</div>
        <div className="mx-auto w-fit rounded bg-white p-2"><QRCodeSVG value={url} size={140} /></div>
        <p className="text-xs text-muted-foreground">Historial público por número de serie: reparaciones, repuestos, garantía y bench. No muestra datos del dueño.</p>
        <div className="flex flex-wrap justify-center gap-1">
          <Button size="sm" variant="outline" onClick={() => { void navigator.clipboard?.writeText(url); toast.success('Enlace copiado'); }}><Copy className="mr-1 h-3.5 w-3.5" /> Copiar</Button>
          <a href={urlWhatsApp(o.cliente.celular, `El pasaporte de tu ${equipo}: ${url}`)} target="_blank" rel="noreferrer"><Button size="sm"><MessageCircle className="mr-1 h-3.5 w-3.5" /> WhatsApp</Button></a>
          <Button size="sm" variant="outline" onClick={() => setSticker(true)}><Printer className="mr-1 h-3.5 w-3.5" /> Sticker</Button>
        </div>
        <a href={url} target="_blank" rel="noreferrer" className="block text-xs text-primary underline">Ver el pasaporte</a>
      </CardContent></Card>
      <div className="space-y-3">
        <Card><CardContent className="space-y-2 p-4 text-sm">
          <div className="font-semibold">Traspaso (venta del equipo usado)</div>
          <p className="text-xs text-muted-foreground">El dueño actual autoriza con un código (vale 7 días, 5 intentos). El comprador lo reclama en el pasaporte o aquí en el mostrador. La garantía vigente viaja con el equipo.</p>
          {f.traspaso
            ? <div className="rounded-md bg-muted/50 p-2">Código activo: <span className="font-mono text-lg font-bold tracking-widest">{f.traspaso.codigo}</span> · vence {fmtFechaHora(f.traspaso.expira)}</div>
            : <Button size="sm" variant="outline" isLoading={generar.isPending} onClick={() => generar.mutate()}>Generar código de traspaso</Button>}
          <div className="flex flex-wrap items-end gap-2 border-t pt-2">
            {nuevo
              ? <div className="flex items-center gap-2 rounded border px-2 py-1">Nuevo dueño: <strong>{nuevo.nombre}</strong><button type="button" aria-label="Cambiar" onClick={() => setNuevo(null)}>✕</button></div>
              : <div className="relative">
                  <input className={`${fieldCls} w-64`} placeholder="Buscar nuevo dueño (cliente)" value={q} onChange={(e) => setQ(e.target.value)} />
                  {clientes.length > 0 && <div className="absolute z-10 mt-1 max-h-48 w-80 overflow-y-auto rounded border bg-card shadow">
                    {clientes.map((c) => <button key={c.id} type="button" className="block w-full px-2 py-1 text-left text-xs hover:bg-muted" onClick={() => setNuevo({ id: c.id, nombre: c.nombre })}>{c.nombre} · {c.identificacion}</button>)}
                  </div>}
                </div>}
            <input className={`${fieldCls} w-32 font-mono uppercase`} placeholder="Código" value={codigo} onChange={(e) => setCodigo(e.target.value)} />
            <Button size="sm" disabled={!nuevo || codigo.trim().length < 6} isLoading={aplicar.isPending} onClick={() => aplicar.mutate()}>Registrar traspaso</Button>
          </div>
        </CardContent></Card>
        <Card><CardContent className="overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-2 pl-3 pr-2">N°</th><th className="py-2 pr-2">Dueño</th><th className="py-2 pr-2">Desde</th><th className="py-2 pr-2">Hasta</th><th className="py-2 pr-3">Cómo</th></tr></thead>
            <tbody>{f.duenos.map((d, i) => (
              <tr key={i} className="border-b last:border-0">
                <td className="py-1.5 pl-3 pr-2 text-muted-foreground">{i + 1}</td>
                <td className="py-1.5 pr-2">{d.nombre} <span className="text-xs text-muted-foreground">{d.identificacion}</span></td>
                <td className="py-1.5 pr-2">{fmtFecha(d.desde)}</td><td className="py-1.5 pr-2">{d.hasta ? fmtFecha(d.hasta) : 'actual'}</td>
                <td className="py-1.5 pr-3 text-xs">{d.motivo === 'alta' ? 'Primera recepción' : d.motivo === 'traspaso' ? `Traspaso${d.canal ? ` (${d.canal})` : ''}` : 'Cambio detectado en recepción'}</td>
              </tr>))}
            </tbody>
            <tfoot><tr className="border-t-2 bg-muted/30 font-semibold"><td colSpan={5} className="py-2 pl-3">{f.duenos.length} dueño(s) registrado(s)</td></tr></tfoot>
          </table>
        </CardContent></Card>
      </div>
    </div>
  );
}

// ── Fotos ──────────────────────────────────────────────────────────────────
function Foto({ id, mime }: { id: string; mime: string }) {
  const { data } = useQuery<{ mime: string; base64: string }>({ queryKey: [QK, 'foto', id], queryFn: () => tallerRpc('foto', { _id: id }), staleTime: Infinity });
  return data ? <img src={`data:${data.mime || mime};base64,${data.base64}`} alt="Foto de la orden" className="h-40 w-full rounded object-cover" /> : <div className="h-40 animate-pulse rounded bg-muted" />;
}
function FotosTab({ o }: { o: OrdenDetalle }) {
  const qc = useQueryClient();
  const [desc, setDesc] = useState('');
  const [publica, setPublica] = useState(true);
  const [subiendo, setSubiendo] = useState(false);
  const certFoto = async (id: string, valor: boolean) => {
    try { await tallerRpc('foto_certificado', { _foto: id, _valor: valor }); void qc.invalidateQueries({ queryKey: [QK, 'orden', o.id] }); }
    catch (e) { toast.error(getErrorMessage(e)); }
  };
  const subir = async (files: FileList | null) => {
    if (!files?.length) return;
    setSubiendo(true);
    try {
      for (const f of Array.from(files)) {
        const datos = await comprimirFoto(f);
        await tallerRpc('foto_subir', { _orden: o.id, _etapa: o.estado, _datos: datos, _mime: 'image/jpeg', _descripcion: desc || null, _publica: publica });
      }
      toast.success('Foto(s) agregada(s)' + (publica ? ': el cliente las ve en el portal' : ''));
      setDesc(''); void qc.invalidateQueries({ queryKey: [QK, 'orden', o.id] });
    } catch (e) { toast.error(getErrorMessage(e)); } finally { setSubiendo(false); }
  };
  return (
    <div className="space-y-3">
      <Card><CardContent className="flex flex-wrap items-end gap-2 p-4 text-sm">
        <Campo label="Descripción"><input className={`${fieldCls} w-72`} value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="p. ej. placa abierta, puerto nuevo soldado" /></Campo>
        <label className="flex items-center gap-1 pb-2 text-xs"><input type="checkbox" checked={publica} onChange={(e) => setPublica(e.target.checked)} /> Visible para el cliente</label>
        <label className={`inline-flex cursor-pointer items-center gap-1 rounded-md bg-primary px-3 py-2 text-sm text-primary-foreground ${subiendo ? 'opacity-60' : ''}`}>
          <Camera className="h-4 w-4" /> {subiendo ? 'Subiendo…' : 'Tomar / subir foto'}
          <input type="file" accept="image/*" capture="environment" multiple className="hidden" disabled={subiendo} onChange={(e) => void subir(e.target.files)} />
        </label>
      </CardContent></Card>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {o.fotos.map((f) => (
          <div key={f.id} className="rounded-lg border p-1">
            <Foto id={f.id} mime={f.mime} />
            <div className="mt-1 flex items-center gap-1 px-1 text-xs">
              {f.publica ? <Eye className="h-3 w-3 text-emerald-600" /> : <EyeOff className="h-3 w-3 text-muted-foreground" />}
              <span className="truncate">{f.descripcion ?? ESTADOS[f.etapa as EstadoOrden]?.label ?? f.etapa}</span>
            </div>
            {['image/jpeg', 'image/png'].includes(f.mime) && (
              <label className="flex items-center gap-1 px-1 text-[11px] text-violet-700 dark:text-violet-300">
                <input type="checkbox" checked={!!f.en_certificado} onChange={(e) => void certFoto(f.id, e.target.checked)} /> En el certificado de bench
              </label>
            )}
          </div>
        ))}
        {o.fotos.length === 0 && <div className="col-span-full py-6 text-center text-sm text-muted-foreground">Sin fotos. Las de recepción protegen al taller ante reclamos («ya venía rayado»).</div>}
      </div>
    </div>
  );
}

// ── Recepción e historia ───────────────────────────────────────────────────
function RecepcionTab({ o }: { o: OrdenDetalle }) {
  const si = (v: unknown) => (v === true ? 'Sí' : v === false ? 'No' : '—');
  return (
    <div className="grid gap-3 lg:grid-cols-3">
      <Card><CardContent className="space-y-1 p-4 text-sm">
        <div className="font-semibold">Accesorios entregados</div>
        {o.accesorios.length ? o.accesorios.map((a) => <div key={a}>• {a}</div>) : <div className="text-muted-foreground">Ninguno</div>}
        <div className="pt-2 font-semibold">Estado físico</div>
        {CHECKS_FISICOS.map(([k, l]) => <div key={k} className="flex justify-between"><span>{l}</span><span>{si(o.estado_fisico[k])}</span></div>)}
        {o.estado_fisico.notas ? <div className="text-xs text-muted-foreground">{String(o.estado_fisico.notas)}</div> : null}
      </CardContent></Card>
      <Card><CardContent className="space-y-1 p-4 text-sm">
        <div className="font-semibold">Prueba de encendido (delante del cliente)</div>
        {PRUEBAS_ENCENDIDO.map(([k, l]) => <div key={k} className="flex justify-between"><span>{l}</span><span>{si(o.prueba_encendido[k])}</span></div>)}
        <div className="pt-2 text-xs text-muted-foreground">Consentimiento: {fmtFechaHora(o.consentimiento_at)} · firma {o.tiene_firma ? 'registrada' : 'no registrada'}</div>
      </CardContent></Card>
      <Card><CardContent className="space-y-1 p-4 text-sm">
        <div className="flex items-center gap-1 font-semibold"><ClipboardList className="h-4 w-4" /> Historial del equipo</div>
        {o.historial.length === 0 && <div className="text-muted-foreground">Primera vez en el taller.</div>}
        {o.historial.map((h) => (
          <div key={h.id} className="rounded border p-2 text-xs">
            <div className="font-mono font-semibold">{h.codigo} · {ESTADOS[h.estado].label}</div>
            <div>{fmtFecha(h.fecha)} · {h.falla}</div>
            {h.trabajo && <div className="text-muted-foreground">{h.trabajo}</div>}
            {h.garantia_hasta && <div className="text-emerald-700">Garantía hasta {fmtFecha(h.garantia_hasta)}</div>}
          </div>
        ))}
      </CardContent></Card>
    </div>
  );
}

function HistoriaTab({ o, abierta, onNota }: { o: OrdenDetalle; abierta: boolean; onNota: (nota: string, publica: boolean) => void }) {
  const [nota, setNota] = useState('');
  const [publica, setPublica] = useState(false);
  return (
    <Card><CardContent className="space-y-3 p-4 text-sm">
      {abierta && (
        <div className="flex flex-wrap items-end gap-2">
          <input className={`${fieldCls} min-w-[280px] flex-1`} placeholder="Agregar una nota" value={nota} onChange={(e) => setNota(e.target.value)} />
          <label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={publica} onChange={(e) => setPublica(e.target.checked)} /> Visible para el cliente</label>
          <Button size="sm" disabled={!nota.trim()} onClick={() => { onNota(nota.trim(), publica); setNota(''); }}>Agregar</Button>
        </div>
      )}
      <ol className="space-y-2 border-l-2 pl-4">
        {o.eventos.slice().reverse().map((e, i) => (
          <li key={i} className="relative">
            <span className={`absolute -left-[21px] top-1 h-3 w-3 rounded-full ${e.estado ? 'bg-primary' : 'bg-muted-foreground/40'}`} />
            <div className="text-xs text-muted-foreground">{fmtFechaHora(e.fecha)}{e.usuario ? ` · ${e.usuario}` : ''}{e.publica ? '' : ' · interna'}</div>
            <div>{e.estado && <span className={`mr-1 rounded px-1.5 text-[11px] ${ESTADOS[e.estado as EstadoOrden]?.cls ?? ''}`}>{ESTADOS[e.estado as EstadoOrden]?.label ?? e.estado}</span>}{e.nota}</div>
          </li>
        ))}
      </ol>
    </CardContent></Card>
  );
}

// ── Ticket imprimible (térmico) con QR del portal ──────────────────────────
export function TicketOrden({ o, nombre, terminos, onClose }: {
  o: Pick<OrdenDetalle, 'codigo' | 'token' | 'created_at' | 'falla_reportada' | 'accesorios' | 'fecha_prometida'> & { cliente: { nombre: string; identificacion: string; celular: string | null }; equipo: { marca: string | null; modelo: string | null; serial: string | null } };
  nombre: string; terminos: string | null; onClose: () => void;
}) {
  const url = urlPitStop(o.token);
  return (
    <div className="space-y-3 p-4 sm:p-6">
      <div className="no-print flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={onClose}><ArrowLeft className="mr-1 h-4 w-4" /> Volver</Button>
        <PrintButton />
      </div>
      <div className="mx-auto max-w-[320px] rounded border bg-white p-3 font-mono text-[12px] leading-snug text-black">
        <Imprimible titulo={nombre} subtitulo={`Orden de servicio ${o.codigo}`}>
          <div>Fecha: {fmtFechaHora(o.created_at)}</div>
          <div>Cliente: {o.cliente.nombre}</div>
          <div>CI/RUC: {o.cliente.identificacion}</div>
          <div>Equipo: {[o.equipo.marca, o.equipo.modelo].filter(Boolean).join(' ')}</div>
          <div>Serie: {o.equipo.serial ?? '—'}</div>
          <div className="mt-1">Falla: {o.falla_reportada}</div>
          <div>Accesorios: {o.accesorios.join(', ') || 'ninguno'}</div>
          <div>Fecha estimada: {fmtFecha(o.fecha_prometida)}</div>
          <div className="my-2 flex flex-col items-center">
            <QRCodeSVG value={url} size={140} />
            <div className="mt-1 text-center text-[11px]">Escanee para seguir su reparación en vivo (Pit Stop)</div>
          </div>
          {terminos && <div className="whitespace-pre-line border-t pt-1 text-[10px]">{terminos}</div>}
          <div className="mt-6 border-t pt-1 text-center text-[10px]">Firma del cliente</div>
          <div className="mt-3 border-t-2 border-dashed pt-2 text-center text-[11px]">— Etiqueta del equipo —<br /><strong>{o.codigo}</strong> · {o.cliente.nombre.split(' ')[0]}<br />{o.equipo.serial ?? ''}</div>
        </Imprimible>
      </div>
    </div>
  );
}
